import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  TextField,
  MenuItem,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  Alert,
  CircularProgress,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Add, Edit, Delete, Visibility, Upload } from '@mui/icons-material';
import { useNavigate, Link } from 'react-router-dom';
import { Candidate, PaginationParams } from '../types';
import { candidatesApi, piProfilesApi, jobsApi, piSyncApi } from '../services/api';
import { useNotification } from '../contexts/NotificationContext';
import { useAuth } from '../contexts/AuthContext';
import { Sync } from '@mui/icons-material';

const Candidates: React.FC = () => {
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    pages: 0,
  });
  const [filters, setFilters] = useState<PaginationParams>({
    status: '',
    search: '',
    sortBy: 'created_at',
    sortOrder: 'desc',
    unassociated: undefined,
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [candidateToDelete, setCandidateToDelete] = useState<string | null>(null);
  const [selectedCandidates, setSelectedCandidates] = useState<(string | number)[]>([]);
  const [bulkSyncDialogOpen, setBulkSyncDialogOpen] = useState(false);
  const [isBulkSyncing, setIsBulkSyncing] = useState(false);
  const [showUnassociated, setShowUnassociated] = useState(false);

  const { showNotification } = useNotification();
  const { user } = useAuth();
  const navigate = useNavigate();

  const canEdit = user?.role && ['admin', 'hr', 'manager'].includes(user.role);
  const canDelete = user?.role && ['admin', 'hr'].includes(user.role);

  const fetchCandidates = React.useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        ...filters,
        page: pagination.page,
        limit: pagination.limit,
        unassociated: showUnassociated ? 'true' : undefined,
      };
      
      const response = await candidatesApi.getAll(params);
      setCandidates(response.data.candidates);
      setPagination(prev => ({
        ...prev,
        ...response.data.pagination,
      }));
    } catch (error) {
      showNotification('Failed to load candidates', 'error');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filters, showNotification, showUnassociated]);

  const fetchPiProfiles = async () => {
    try {
      await piProfilesApi.getAll();
      // Will use PI profiles in future features
    } catch (error) {
      console.error('Failed to load PI profiles:', error);
    }
  };

  const fetchJobs = async () => {
    try {
      const response = await jobsApi.getAll();
      // setJobs(response.data.jobs);
      console.log('Jobs loaded:', response.data.jobs.length);
    } catch (error) {
      console.error('Failed to load jobs:', error);
    }
  };

  // Initial load - fetch PI profiles and jobs only once
  useEffect(() => {
    fetchPiProfiles();
    fetchJobs();
  }, []);

  // Fetch candidates whenever dependencies change
  useEffect(() => {
    fetchCandidates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.limit, filters, showUnassociated]);

  const handleFilterChange = (field: string, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };


  const handlePaginationModelChange = (model: { page: number; pageSize: number }) => {
    setPagination(prev => ({
      ...prev,
      page: model.page + 1,
      limit: model.pageSize,
    }));
  };

  const handleDeleteClick = (id: string) => {
    setCandidateToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!candidateToDelete) return;

    try {
      await candidatesApi.delete(candidateToDelete);
      showNotification('Candidate archived successfully', 'success');
      fetchCandidates();
    } catch (error) {
      showNotification('Failed to archive candidate', 'error');
    } finally {
      setDeleteDialogOpen(false);
      setCandidateToDelete(null);
    }
  };

  const handleSingleCandidateSync = async (candidateId: string) => {
    try {
      const response = await piSyncApi.syncCandidate(candidateId);
      
      if (response.data.success) {
        showNotification('Candidate synced successfully', 'success');
        fetchCandidates();
      } else {
        showNotification(response.data.message || 'Candidate not found in PI', 'warning');
      }
    } catch (error) {
      showNotification('Failed to sync candidate', 'error');
    }
  };

  const handleBulkSync = async () => {
    if (selectedCandidates.length === 0) return;

    try {
      setIsBulkSyncing(true);
      const candidateIds = Array.from(selectedCandidates).map(id => parseInt(id.toString()));
      const response = await piSyncApi.syncCandidatesBulk(candidateIds);
      
      const { synced, notFound, errors } = response.data.results;
      
      let message = `Synced: ${synced.length} candidates`;
      if (notFound.length > 0) {
        message += `, Not found in PI: ${notFound.length}`;
      }
      if (errors.length > 0) {
        message += `, Errors: ${errors.length}`;
      }
      
      showNotification(message, synced.length > 0 ? 'success' : 'warning');
      setBulkSyncDialogOpen(false);
      setSelectedCandidates([]);
      fetchCandidates();
    } catch (error) {
      showNotification('Failed to sync candidates', 'error');
    } finally {
      setIsBulkSyncing(false);
    }
  };

  const handleShowUnassociatedToggle = () => {
    setShowUnassociated(!showUnassociated);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'in_progress': return 'info';
      case 'completed': return 'success';
      case 'hired': return 'success';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Link
          to={`/candidates/${params.row.id}`}
          style={{ textDecoration: 'none' }}
        >
          <Typography
            sx={{
              color: 'primary.main',
              '&:hover': { textDecoration: 'underline' },
              cursor: 'pointer',
            }}
          >
            {params.row.first_name} {params.row.last_name}
          </Typography>
        </Link>
      ),
    },
    {
      field: 'job_title',
      headerName: 'Job',
      flex: 1,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams) => (
        params.value ? (
          <Typography>{params.value}</Typography>
        ) : (
          <Typography color="text.secondary" fontStyle="italic">
            No job assigned
          </Typography>
        )
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value}
          size="small"
          color={getStatusColor(params.value) as any}
          clickable={false}
        />
      ),
    },
    {
      field: 'pi_profile_name',
      headerName: 'PI Profile',
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        params.value ? (
          <Chip
            label={params.row.pi_profile_code || params.value}
            size="small"
            variant="outlined"
            clickable={false}
          />
        ) : null
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 180,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <IconButton
            size="small"
            onClick={() => navigate(`/candidates/${params.row.id}`)}
            data-testid={`view-candidate-${params.row.id}`}
            title="View"
          >
            <Visibility />
          </IconButton>
          {canEdit && (
            <>
              <IconButton
                size="small"
                onClick={() => navigate(`/candidates/${params.row.id}`)}
                data-testid={`edit-candidate-${params.row.id}`}
                title="Edit"
              >
                <Edit />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => handleSingleCandidateSync(params.row.id)}
                data-testid={`sync-candidate-${params.row.id}`}
                title="Sync from PI"
              >
                <Sync />
              </IconButton>
            </>
          )}
          {canDelete && (
            <IconButton
              size="small"
              onClick={() => handleDeleteClick(params.row.id)}
              color="error"
              data-testid={`delete-candidate-${params.row.id}`}
              title="Delete"
            >
              <Delete />
            </IconButton>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }} data-testid="candidates-page">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" data-testid="page-title">Candidates</Typography>
        {canEdit && (
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              startIcon={<Upload />}
              onClick={() => navigate('/candidates/import')}
              data-testid="import-button"
            >
              Manual Import
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate('/candidates/new')}
              data-testid="add-candidate-button"
            >
              Add Candidate
            </Button>
          </Box>
        )}
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <TextField
            label="Search"
            variant="outlined"
            size="small"
            value={filters.search || ''}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            sx={{ minWidth: 200 }}
            data-testid="search-input"
          />
          <TextField
            select
            label="Status"
            variant="outlined"
            size="small"
            value={filters.status || ''}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            sx={{ minWidth: 150 }}
            data-testid="status-filter"
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="in_progress">In Progress</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="hired">Hired</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
          </TextField>
          <Box display="flex" alignItems="center" gap={1}>
            <Checkbox
              checked={showUnassociated}
              onChange={handleShowUnassociatedToggle}
              size="small"
              data-testid="unassociated-filter"
            />
            <Typography variant="body2">
              Show only unassociated candidates
            </Typography>
          </Box>
        </Box>
        
        {/* Bulk sync temporarily disabled - will re-implement with proper selection */}
        {/* {selectedCandidates.length > 0 && (
          <Box mt={2}>
            <Alert severity="info">
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography>
                  {selectedCandidates.length} candidate(s) selected
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Sync />}
                  onClick={() => setBulkSyncDialogOpen(true)}
                >
                  Sync from PI
                </Button>
              </Box>
            </Alert>
          </Box>
        )} */}
      </Paper>

      <Paper sx={{ 
        height: 'calc(100vh - 280px)', 
        width: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <DataGrid
          rows={candidates}
          columns={columns}
          loading={loading}
          pagination
          paginationMode="server"
          paginationModel={{
            page: pagination.page - 1,
            pageSize: pagination.limit,
          }}
          pageSizeOptions={[5, 10, 25, 50, 100]}
          rowCount={pagination.total}
          onPaginationModelChange={handlePaginationModelChange}
          disableRowSelectionOnClick
          sx={{
            border: 0,
            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
          }}
          data-testid="candidates-grid"
          getRowId={(row) => row.id}
          slotProps={{
            row: {
              'data-testid': 'candidate-row',
            },
          }}
        />
      </Paper>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Archive Candidate</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to archive this candidate? This action can be undone later.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Archive
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Sync Dialog */}
      <Dialog open={bulkSyncDialogOpen} onClose={() => setBulkSyncDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Sync Candidates from PI</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            This will sync {selectedCandidates.length} selected candidate(s) with their PI assessment data.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The sync will:
          </Typography>
          <ul style={{ marginTop: 8, marginBottom: 16 }}>
            <li><Typography variant="body2">Update PI profile information</Typography></li>
            <li><Typography variant="body2">Retrieve behavioral assessment results</Typography></li>
            <li><Typography variant="body2">Retrieve cognitive assessment scores (if available)</Typography></li>
          </ul>
          <Typography variant="body2" color="warning.main">
            Note: Candidates must have completed assessments in PI to sync data.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkSyncDialogOpen(false)} disabled={isBulkSyncing}>
            Cancel
          </Button>
          <Button 
            onClick={handleBulkSync} 
            variant="contained" 
            disabled={isBulkSyncing}
            startIcon={isBulkSyncing ? <CircularProgress size={20} /> : <Sync />}
          >
            {isBulkSyncing ? 'Syncing...' : 'Sync'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Candidates;